<template>
    <div class="w-fullcontent-start gap-xl hide-scrollbar grid grid-cols-8 items-start">
        <form id="info-form" class="gap-lg px-md col-span-5 flex flex-col">
            <h1 class="mb-md">Organization info</h1>

            <div class="input-group">
                <label for="org-name" class="required">Name</label>
                <div class="input-wrapper bg-lightest" :class="{ 'pointer-events-none': locked }">
                    <input
                        id="org-name"
                        v-model="orgName"
                        :autofocus="false"
                        placeholder="Type organization name"
                        type="text"
                        :disabled="locked"
                    />
                </div>
                <p v-if="errorPool.orgName" class="error">{{ errorPool.orgName }}</p>
            </div>

            <InputSelect
                :value="orgType"
                :options="orgTypeOptions"
                :label="'Organization type'"
                :placeholder="'Select organization type'"
                :disabled="locked"
                :required="true"
                :unique-key="1"
                @update="setOrgType"
            />

            <div class="input-group flex w-full flex-col [&>ul]:w-full">
                <label for="customerFootprint" class="required">Organization Footprint</label>
                <CascadingUUIDLocations
                    name="customerFootprint"
                    :items="locationOptions"
                    :associated-items="associatedLocations"
                    :editable="!locked"
                    :check-recursively="false"
                    :show-uneditable="true"
                    @update="selectLocations"
                    @applied-association="selectLocations"
                />

                <p v-if="errorPool.orgLocation" class="error">
                    {{ errorPool.orgLocation }}
                </p>
            </div>

            <div v-if="serviceDeskOptions" class="input-group">
                <label for="serviceDeskDropdown" class="required">Service desks</label>
                <ServiceDeskDropdown
                    :associated-desks="associatedServiceDesks"
                    :desks="serviceDeskOptions"
                    :chosen-service-desks="chosenServiceDesks"
                    :locked-service-desks="lockedServiceDeskDefaults"
                    :locked="locked"
                    @update="setChosenServiceDesks"
                />
                <p v-if="errorPool.serviceDeskValue" class="error">
                    {{ errorPool.serviceDeskValue }}
                </p>
            </div>

            <div class="input-group">
                <label for="description">Description</label>
                <div class="input-wrapper">
                    <textarea id="description" v-model="orgDescription" class="h-40" placeholder="Org description" />
                </div>
            </div>

            <div class="gap-y-md gap-x-sm grid grid-cols-2">
                <h2 class="col-span-2">Primary contact</h2>

                <div class="input-group">
                    <label for="email-address" class="required">Email address</label>
                    <div class="input-wrapper bg-lightest">
                        <input id="email-address" v-model="email" placeholder="example@atnorth.com" type="email" />
                    </div>
                    <p v-if="errorPool.email" class="error">{{ errorPool.email }}</p>
                </div>
                <div class="input-group">
                    <label for="phone-number">Phone number</label>
                    <div class="input-wrapper bg-lightest">
                        <IntlTelInput
                            :options="{ initialCountry: 'is' }"
                            @change-number="setPhoneNumber"
                            @change-validity="setValidPhoneNumber"
                        />
                    </div>
                    <p v-if="errorPool.phoneNumber" class="error">
                        {{ errorPool.phoneNumber }}
                    </p>
                </div>
                <div class="input-group">
                    <label for="first-name">First name</label>
                    <div class="input-wrapper bg-lightest">
                        <input
                            id="first-name"
                            v-model="firstName"
                            :autofocus="false"
                            placeholder="Enter first name"
                            type="text"
                        />
                    </div>
                    <p v-if="errorPool.firstName" class="error">
                        {{ errorPool.firsName }}
                    </p>
                </div>
                <div class="input-group">
                    <label for="last-name">Last name</label>
                    <div class="input-wrapper bg-lightest">
                        <input
                            id="org-name"
                            v-model="lastName"
                            :autofocus="false"
                            placeholder="Enter last name"
                            type="text"
                        />
                    </div>
                    <p v-if="errorPool.lastName" class="error">
                        {{ errorPool.lastName }}
                    </p>
                </div>
            </div>
        </form>
    </div>
    <BottomButtons
        arrow
        :handle-submit="handleSubmit"
        :disabled="disable || allowedSubmit"
        :sticky="false"
        dont-disable
        :loading="loading"
        @cancel="emit('cancel')"
    />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, type ComputedRef, type PropType } from 'vue';
import InputSelect from '@/components/support/input-select.vue';
import type {
    AdminOrganizationFormData,
    AdminOrgInfoBody,
    AdminServiceDeskInfo,
    AdminServiceDeskReadOnly,
    Option,
    OrganizationType,
    UUIDLocation,
} from '@/stores/store-interfaces';
import { useAdminStore } from '@/stores/admin';
import { getLocationByLocationIdFromUUIDCascade, mapRoleLevelToName } from '@/stores/helpers';
import BottomButtons from '@/components/bottom-buttons.vue';
import CascadingUUIDLocations from '@/components/misc/cascading-locations-uuid.vue';
import ServiceDeskDropdown from '@/components/misc/service-desk-dropdown.vue';
import {
    buildEditableServiceDeskDefaultsForOrganizationType,
    buildLockedIncidentServiceDeskDefaultsForOrganizationType,
    getLockedDefaultsRole,
    mergeServiceDeskSelections,
} from '@/components/admin/organization/creation-process/organization-type-service-desk-defaults';
// @ts-expect-error intl-tel-input ships no types for the vueWithUtils entry
import IntlTelInput from 'intl-tel-input/vueWithUtils';
import 'intl-tel-input/styles';

const props = defineProps({
    locked: Boolean,
    formInfo: Object as PropType<AdminOrganizationFormData>,
    desks: Array as PropType<AdminServiceDeskReadOnly[]>,
    disable: Boolean,
    loading: Boolean,
});

const adminStore = useAdminStore();

const orgName = ref<string>('');

const allowedSubmit = computed(() => {
    const allowed =
        !!chosenLevelId.value &&
        !!orgName.value &&
        !!orgType.value.length &&
        !!email.value &&
        !!checkedLocations.value.length &&
        !!getSelectedServiceDesks().length;
    return !allowed;
});

const orgType = ref<Option[]>([]);
const orgTypeOptions = ref<Option[]>([]);

function setOrgType(value: Option[]) {
    orgType.value = value;
}
const organizationTypes = ref<OrganizationType[]>([]);
const selectedOrganizationType = computed(() =>
    organizationTypes.value.find((item) => item.organization_type_id === orgType.value[0]?.value),
);

const levelValue = ref<Option[]>([]);
const levelOptions = ref<Option[]>([]);
const chosenLevelId = ref<AdminOrgInfoBody['organization_creation_form']['service_level_id']>(1);

const locationOptions = ref<UUIDLocation[]>([]);
const associatedLocations = ref<UUIDLocation[]>([]);
const checkedLocations = ref<AdminOrgInfoBody['locations']>([]);

const serviceDeskOptions = ref<AdminServiceDeskInfo[]>([]);
const chosenServiceDesks = ref<AdminOrgInfoBody['service_desks']>([]);
const associatedServiceDesks = ref<AdminOrgInfoBody['service_desks']>([]);

const orgDescription = ref<string>('');
const email = ref<string>('');
const phoneNumber = ref<string>();
const validPhoneNumber = ref<boolean>(false);

function setPhoneNumber(value: string) {
    phoneNumber.value = value;
}

function setValidPhoneNumber(value: boolean) {
    validPhoneNumber.value = value;
}
const firstName = ref<string>('');
const lastName = ref<string>('');

const errorPool = ref<Record<string, string>>({});
const selectedLockedDefaultsRole = computed(() => getLockedDefaultsRole(selectedOrganizationType.value));
const lockedServiceDeskDefaults = computed(() =>
    selectedLockedDefaultsRole.value
        ? buildLockedIncidentServiceDeskDefaultsForOrganizationType(
              selectedLockedDefaultsRole.value,
              serviceDeskOptions.value,
          )
        : [],
);
const editableServiceDeskDefaults = computed(() =>
    selectedLockedDefaultsRole.value
        ? buildEditableServiceDeskDefaultsForOrganizationType(selectedLockedDefaultsRole.value, serviceDeskOptions.value)
        : [],
);

watch(
    () => props.formInfo,
    () => {
        populateFormData();
    },
);

function setChosenServiceDesks(value: AdminOrgInfoBody['service_desks']) {
    chosenServiceDesks.value = mergeServiceDeskSelections(lockedServiceDeskDefaults.value, value);
}

watch(
    () => [selectedLockedDefaultsRole.value, serviceDeskOptions.value],
    () => {
        if (selectedLockedDefaultsRole.value) {
            chosenServiceDesks.value = mergeServiceDeskSelections(
                lockedServiceDeskDefaults.value,
                editableServiceDeskDefaults.value,
            );
        } else {
            chosenServiceDesks.value = [];
        }
    },
    { deep: true },
);

onMounted(() => {
    populateFormData();

    setupOptions();
});

const selectLocations = (locations: Set<string>) => {
    checkedLocations.value = [];
    locations.forEach((location) => {
        const selectedLocation =
            getLocationByLocationIdFromUUIDCascade(location, locationOptions.value) ??
            getLocationByLocationIdFromUUIDCascade(location, associatedLocations.value);
        if (!selectedLocation) return;

        checkedLocations.value.push({
            locationId: selectedLocation.id,
            locationType: selectedLocation.locationType,
        });
    });
};

const populateFormData = async () => {
    if (props?.formInfo?.organization_creation_form_id !== undefined) {
        const serviceLevel = props.formInfo.organization.service_level_id;
        const levelName = mapRoleLevelToName(serviceLevel) ?? '';
        orgName.value = props.formInfo.organization.name;
        orgType.value = props.formInfo.organization_type_id
            ? [
                  {
                      value: props.formInfo.organization_type_id,
                      label: props.formInfo.organization.organization_type.name,
                  },
              ]
            : [];
        chosenLevelId.value = props.formInfo.organization.service_level_id;
        levelValue.value = [
            {
                value: serviceLevel,
                label: levelName,
                dropdownClass: `bg-${levelName}-100 text-${levelName}-500 font-bold px-sm py-2xs rounded-sm`,
                placeholderClass: `bg-${levelName}-100 text-${levelName}-500 font-bold px-sm py-2xs rounded-sm m-xsm w-fit absolute top-[4px] left-[14px]`,
            },
        ];
        const organization = await adminStore.getOrganizationById(props.formInfo.organization_id);
        associatedLocations.value = await adminStore.getLocationsByOrgId(organization.organization_uuid);
        checkedLocations.value = associatedLocations.value.map((location) => ({
            locationId: location.id,
            locationType: location.locationType,
        }));

        orgDescription.value = props.formInfo.organization.description ?? '';
        associatedServiceDesks.value =
            props.desks?.map((desk) => {
                return {
                    service_desk_id: desk.id,
                    isChecked: true,
                    name: desk.name,
                    request_types:
                        desk.request_types?.map((req) => {
                            return {
                                request_type_id: req.id,
                                isChecked: true,
                                name: req.name,
                            };
                        }) ?? [],
                };
            }) ?? [];

        email.value = props.formInfo.contact_email;
        phoneNumber.value = props.formInfo.contact_phone;
        firstName.value = props.formInfo.contact_first_name;
        lastName.value = props.formInfo.contact_last_name;
    }
};

const setupOptions = async () => {
    locationOptions.value = (await adminStore.getAllLocations()) as UUIDLocation[];

    levelOptions.value = await adminStore.getServiceLevelOptions().then((data) =>
        data.map((item) => {
            return {
                value: item.service_level_id,
                label: item.name,
                dropdownClass: `bg-${item.name.toLowerCase()}-100 text-${item.name.toLowerCase()}-500 font-bold px-sm py-2xs rounded-sm`,
                placeholderClass: `bg-${item.name.toLowerCase()}-100 text-${item.name.toLowerCase()}-500 font-bold px-sm py-2xs rounded-sm m-xsm w-fit absolute top-[4px] left-[14px]`,
            };
        }),
    );

    serviceDeskOptions.value = await adminStore.getServiceDeskOptions();

    organizationTypes.value = await adminStore.getOrganizationTypes();
    orgTypeOptions.value = organizationTypes.value
        .map((item) => ({
            value: item.organization_type_id,
            label: item.name,
        }))
        .filter((item) => item.label !== 'internal');
};

const formData: ComputedRef<AdminOrgInfoBody> = computed(() => ({
    locations: checkedLocations.value,
    service_desks: getSelectedServiceDesks(),
    organization_creation_form: {
        service_level_id: chosenLevelId.value, // currently only uses bronze
        organization_name: orgName.value.trim(),
        organization_type_id: orgType.value[0]?.value ?? null,
        contact_email: email.value.trim(),
        description: orgDescription.value.trim(),
        contact_phone: phoneNumber.value?.trim() ?? '',
        contact_first_name: firstName.value.trim(),
        contact_last_name: lastName.value.trim(),
    },
}));

watch(formData, () => {
    if (Object.keys(errorPool.value).length > 0) {
        validateForm();
    }
});

function validateForm() {
    errorPool.value = {};

    if (orgName.value.trim() === '') {
        errorPool.value.orgName = 'Organization name is required';
    }

    // if (levelValue.value.length === 0) {
    //   errorPool.value.levelValue = "Service level is required";
    // }

    if (!checkedLocations.value.length) {
        errorPool.value.orgLocation = 'Customer footprint is required';
    }

    if (!getSelectedServiceDesks().length) {
        errorPool.value.serviceDeskValue = 'Service desk is required';
    }

    if (email.value.trim() === '') {
        errorPool.value.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
        errorPool.value.email = 'A valid email address is required';
    }

    if (phoneNumber.value && phoneNumber.value?.trim() !== '' && !validPhoneNumber.value) {
        errorPool.value.phoneNumber = 'A valid phone number is required';
    }

    return Object.keys(errorPool.value).length === 0;
}

function getSelectedServiceDesks() {
    return chosenServiceDesks.value.filter(Boolean);
}

function handleSubmit() {
    if (validateForm() === false) {
        return;
    }

    submit();
}

const emit = defineEmits(['submit', 'cancel']);
const submit = () => {
    emit('submit', formData.value);
};
</script>

<style scoped>
@reference '@/assets/styles/default.css';

.info-bar > div {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
}

label {
    display: flex;
    align-items: center;
    margin-bottom: var(--spacing-xs);
}

.required::before {
    content: '*';
    margin-right: var(--spacing-xs);
    color: var(--color-error);
    display: flex;
    align-items: center;
}
</style>
